import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Loader2, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { api } from '@/api/client';

interface OrderStatus {
  id: number;
  name: string;
  code?: string;
  category?: 'forward' | 'rto' | 'delivered' | 'cancelled';
  isPublic?: boolean;
}

const DEFAULTS: OrderStatus[] = [
  { id: 1, name: 'Booking received', code: 'BOOKING_RECEIVED', category: 'forward', isPublic: true },
  { id: 2, name: 'Pickup Schedule', code: 'PICKUP_SCHEDULE', category: 'forward', isPublic: true },
  { id: 3, name: 'Picked', code: 'PICKED', category: 'forward', isPublic: true },
  { id: 4, name: 'At Branch/Warehouse', code: 'AT_BRANCH', category: 'forward', isPublic: true },
  { id: 5, name: 'In Transit', code: 'IN_TRANSIT', category: 'forward', isPublic: true },
  { id: 6, name: 'Arrived at Destination', code: 'ARRIVED', category: 'forward', isPublic: true },
  { id: 7, name: 'Out for Delivery', code: 'OUT_FOR_DELIVERY', category: 'forward', isPublic: true },
  { id: 8, name: 'Delivered', code: 'DELIVERED', category: 'delivered', isPublic: true },
  { id: 9, name: 'Delivery Failed', code: 'DELIVERY_FAILED', category: 'forward', isPublic: true },
  { id: 10, name: 'RTO INTRANSIT', code: 'RTO_INTRANSIT', category: 'rto', isPublic: true },
  { id: 11, name: 'RTO Delivered to Branch/Warehouse', code: 'RTO_AT_BRANCH', category: 'rto', isPublic: true },
  { id: 12, name: 'RTO Delivered to Merchant', code: 'RTO_DELIVERED', category: 'rto', isPublic: true },
  { id: 13, name: 'LOST', code: 'LOST', category: 'forward', isPublic: false },
  { id: 14, name: 'RTO LOST', code: 'RTO_LOST', category: 'rto', isPublic: true },
  { id: 15, name: 'Forward Damage', code: 'FORWARD_DAMAGE', category: 'forward', isPublic: true },
  { id: 16, name: 'RTO Damage', code: 'RTO_DAMAGE', category: 'rto', isPublic: true },
  { id: 17, name: 'Held UP', code: 'HELD_UP', category: 'forward', isPublic: true },
  { id: 18, name: 'RTO Pending', code: 'RTO_PENDING', category: 'rto', isPublic: true },
  { id: 19, name: 'Partially Delivered', code: 'PARTIALLY_DELIVERED', category: 'delivered', isPublic: true },
  { id: 20, name: 'Booking Pending', code: 'BOOKING_PENDING', category: 'forward', isPublic: true },
  { id: 21, name: 'Booking Failed', code: 'BOOKING_FAILED', category: 'cancelled', isPublic: true },
  { id: 23, name: 'Cancelled', code: 'CANCELLED', category: 'cancelled', isPublic: true },
  { id: 24, name: 'NOT Shipped', code: 'NOT_SHIPPED', category: 'cancelled', isPublic: true },
  { id: 25, name: 'Received at Noida Branch', code: 'RECV_NOIDA', category: 'forward', isPublic: true },
  { id: 26, name: 'Wrong Book', code: 'WRONG_BOOK', category: 'forward', isPublic: true },
  { id: 27, name: 'Shipment left for next facility lucknow', code: 'LEFT_LUCKNOW', category: 'forward', isPublic: true },
  { id: 28, name: 'Recevied at Kolkata Branch', code: 'RECV_KOLKATA', category: 'forward', isPublic: true },
  { id: 29, name: 'Recevied at Lucknow Branch', code: 'RECV_LUCKNOW', category: 'forward', isPublic: true },
  { id: 30, name: 'Shipment left for next facility Noida', code: 'LEFT_NOIDA', category: 'forward', isPublic: true },
  { id: 32, name: 'Shipment left for next facility', code: 'LEFT_NEXT', category: 'forward', isPublic: true },
  { id: 33, name: 'Auto Cancelled', code: 'AUTO_CANCELLED', category: 'cancelled', isPublic: false },
];

const CATS = ['forward', 'rto', 'delivered', 'cancelled'] as const;

export function OrderStatusesPanel() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<OrderStatus[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'order_statuses'],
    queryFn: async () => {
      try { return (await api.get<{ items?: OrderStatus[] }>('/settings/order_statuses')).data ?? null; }
      catch { return null; }
    },
  });

  useEffect(() => {
    if (data && Array.isArray((data as any).items)) setRows((data as any).items);
    else if (data === null) setRows([]);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/order_statuses', { items: rows })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'order_statuses'] }); setDirty(false); toast.success('Order statuses saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const update = (idx: number, patch: Partial<OrderStatus>) => {
    setRows(arr => arr.map((r, i) => i === idx ? { ...r, ...patch } : r));
    setDirty(true);
  };
  const remove = (idx: number) => { setRows(arr => arr.filter((_, i) => i !== idx)); setDirty(true); };
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir; if (j < 0 || j >= rows.length) return;
    const next = [...rows]; [next[idx], next[j]] = [next[j], next[idx]]; setRows(next); setDirty(true);
  };
  const add = () => {
    const nextId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
    setRows(arr => [...arr, { id: nextId, name: '', code: '', category: 'forward', isPublic: true }]);
    setDirty(true);
  };
  const loadDefaults = () => { setRows(DEFAULTS); setDirty(true); };


  const doExport = () => {
    if (!rows.length) return;
    const headers = ['id','name','code','category','isPublic'];
    const lines = [headers.join(','), ...rows.map(r => headers.map(h => {
      const v = (r as any)[h];
      const s = v === undefined || v === null ? '' : String(v);
      return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g,'""') + '"' : s;
    }).join(','))];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `order-statuses-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Order Statuses</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Tracking events shown to customers and merchants. <code className="text-xs">id</code> is the lookup key courier integrations use; <code className="text-xs">code</code> is the internal slug.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rows.length === 0 && (
            <button onClick={loadDefaults} className="px-3 py-2 text-sm text-primary underline">
              Load default 31 statuses
            </button>
          )}
          <button onClick={doExport} disabled={!rows.length}
            className="px-3 py-2 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={add} className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Plus size={14} /> Add Status
          </button>
          <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
            className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          No order statuses defined yet. Click <strong>Load default 31 statuses</strong> to start with the standard list, then edit.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-20">Sort</th>
                <th className="text-left px-3 py-2 w-16">ID</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2 w-24">Public</th>
                <th className="px-3 py-2 w-12" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={r.id} onChange={(e) => update(idx, { id: Number(e.target.value) })}
                      className="w-14 px-1.5 py-1 border border-slate-300 rounded text-sm font-mono focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={r.name} onChange={(e) => update(idx, { name: e.target.value })}
                      placeholder="Status name" className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={r.code ?? ''} onChange={(e) => update(idx, { code: e.target.value.toUpperCase() })}
                      placeholder="CODE" className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-mono focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <select value={r.category ?? 'forward'} onChange={(e) => update(idx, { category: e.target.value as any })}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none">
                      {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" checked={r.isPublic !== false} onChange={(e) => update(idx, { isPublic: e.target.checked })} className="rounded" />
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => remove(idx)} className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
